import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  DatePicker,
  Flex,
  FlexItem,
  Form,
  FormGroup,
  FormHelperText,
  HelperText,
  HelperTextItem,
  MenuToggle,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  NumberInput,
  Radio,
  Select,
  SelectList,
  SelectOption,
  Switch,
  TextArea,
  TextInput,
} from '@patternfly/react-core';
import type { MenuToggleElement } from '@patternfly/react-core';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useAdminProducts } from '../../../hooks/useAdminProducts';
import { DECIMAL_RE, HOURLY_RECURRING, formatRecurring } from '../../../utils/money';
import { formatBareDateNumeric } from '../../../utils/dateFormat';
import { SPECIAL_CATEGORY_NAME } from '../../../types/models/SpecialDeal';
import type {
  AdminSpecialDeal,
  SpecialDealCreateRequest,
  SpecialDealKind,
  SpecialDealUpdateRequest,
} from '../../../types/models/SpecialDeal';
import type {
  ApiResponseAdminSpecialCreate,
  ApiResponseAdminSpecialUpdate,
  SpecialDealFieldErrorMap,
} from '../../../types/api/ApiResponseSpecialDeal';

/** A locked attachment for deals created from a parent's page (v1: the VM overview card). */
export interface SpecialDealAttachment {
  parentType: string;
  parentId: number;
  /** Display name for the locked attachment field (e.g. the VM's name). */
  parentName?: string;
}

interface SpecialDealModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after a successful create/update so the caller refreshes its deal list. */
  onSaved: () => void;
  /**
   * The billed organization — the impersonated org's id when staff acts for a tenant. The
   * /admin/billing/specials API takes it in the request BODY (not the impersonation header).
   */
  clientId: number;
  /** Display name for the read-only Organization field. */
  clientName?: string | null;
  /** Edit mode when set (mount keyed by deal id upstream); create mode otherwise. */
  deal?: AdminSpecialDeal;
  /**
   * Prefilled + locked attachment (opened from the VM card). Absent on the Agreements-page
   * create, which is standalone (and therefore charge-only). In edit mode this only supplies the
   * display name — the deal's own parent_* is authoritative and is never re-sent.
   */
  attachment?: SpecialDealAttachment;
}

interface DealFieldErrors {
  productID?: string;
  kind?: string;
  value?: string;
  /** Backend parent_type / parent_id errors, shown under the locked attachment field. */
  attachment?: string;
  next_billing_at?: string;
  occurrences_left?: string;
  note?: string;
  active?: string;
  general?: string;
}

const NOTE_MAX = 255;

/**
 * The shared create/edit form for Special deals (staff-only; POST/PATCH /admin/billing/specials).
 * Used by the Organization → Agreements page (standalone, charge-only) and by the VM overview's
 * Resources card (attached deals, locked to the VM). Client-side checks below MIRROR the backend's
 * cadence/mutability matrix for early feedback — the backend re-validates everything, and its
 * field-keyed errors (JSend `data.{field}`, cross-field under `data.body`) are surfaced verbatim.
 */
function SpecialDealModal({ isOpen, onClose, onSaved, clientId, clientName, deal, attachment }: SpecialDealModalProps) {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();
  // The product select needs the staff catalog (category, active flag, the {{amount}} template
  // check); the modal itself is a staff-only affordance, so the admin GET is always authorized.
  const { products, loading: productsLoading, error: productsError } = useAdminProducts(isOpen);

  const isEdit = deal !== undefined;
  // Identity fields (client, product, kind, attachment) freeze once the deal has billed events —
  // their snapshots reference it (the backend rejects the PATCH; we disable the controls upfront).
  const identityFrozen = isEdit && deal.billed_event_count > 0;

  // In edit mode the deal's own parent is authoritative; the attachment prop then only lends its
  // display name. Attachments are locked in this UI — never editable, never re-sent on PATCH.
  const attachedParent = useMemo(() => {
    if (isEdit) {
      return deal.parent_type !== null && deal.parent_id !== null
        ? { parentType: deal.parent_type, parentId: deal.parent_id, parentName: attachment?.parentName }
        : undefined;
    }
    return attachment;
  }, [isEdit, deal, attachment]);
  const isAttached = attachedParent !== undefined;

  const [productId, setProductId] = useState<number>(deal?.product.id ?? 0);
  const [isProductOpen, setIsProductOpen] = useState(false);
  const [kind, setKind] = useState<SpecialDealKind>(deal?.kind ?? 'charge');
  const [value, setValue] = useState(deal?.value ?? '');
  const [anchor, setAnchor] = useState(deal?.next_billing_at ?? '');
  // Standalone MONTHLY creates use a day-of-month picker instead of a date (the VM-enrollment UX —
  // the only real choice is the anniversary day). Defaults to today's day, clamped to the 1–28 rule.
  const [anchorDay, setAnchorDay] = useState<number | ''>(Math.min(new Date().getDate(), 28));
  const [occurrences, setOccurrences] = useState(
    deal ? (deal.occurrences_left === null ? '' : String(deal.occurrences_left)) : '',
  );
  const [active, setActive] = useState(deal?.active ?? true);
  const [note, setNote] = useState(deal?.note ?? '');
  const [errors, setErrors] = useState<DealFieldErrors>({});
  const [isSaving, setIsSaving] = useState(false);

  // Active products of the "Special" category are the billable set. In edit mode the deal's
  // current product stays listed even if it was since retired or re-categorized (the select may be
  // frozen anyway, but the toggle must still show it). Hourly products are excluded outright: the
  // backend refuses them in Special and on any deal (belt and braces — they live in "GPU Server").
  const specialProducts = useMemo(() => {
    const list = products.filter(
      (p) => p.category.name === SPECIAL_CATEGORY_NAME && p.active && p.recurring !== HOURLY_RECURRING,
    );
    if (deal && !list.some((p) => p.id === deal.product.id)) {
      const current = products.find((p) => p.id === deal.product.id);
      if (current) {
        return [current, ...list];
      }
    }
    return list;
  }, [products, deal]);

  const selectedProduct = specialProducts.find((p) => p.id === productId);
  // Cadence drives the whole form shape; fall back to the deal's embedded ref while the catalog loads.
  const productRecurring =
    selectedProduct?.recurring ?? (deal && productId === deal.product.id ? deal.product.recurring : undefined);
  const selectedProductLabel =
    selectedProduct?.label ?? (deal && productId === deal.product.id ? deal.product.label : undefined);

  const isDiscountKind = kind !== 'charge';
  const isOnceProduct = productRecurring === 'once';
  const isStandalone = !isAttached;
  // A standalone one-time deal bills at creation and is recorded spent — the backend refuses
  // re-arming it, so its budget/active controls are frozen in edit mode.
  const isSpentStandaloneOnce = isEdit && isStandalone && isOnceProduct;
  const anchorRequired = isStandalone && (productRecurring === 'monthly' || productRecurring === 'yearly');

  const clientLabel = clientName ?? deal?.client.name ?? `client #${clientId}`;
  const attachmentDisplay = attachedParent
    ? `Virtual machine — ${attachedParent.parentName ?? `#${attachedParent.parentId}`}`
    : '';

  const clearError = (field: keyof DealFieldErrors) =>
    setErrors((prev) => (prev[field] || prev.general ? { ...prev, [field]: undefined, general: undefined } : prev));

  // ---- Client-side mirrors of the backend rules (early feedback only; backend re-validates) ----

  // The template mirrors only gate a NEW binding (create, or an edit that changes product/kind):
  // an existing deal whose product template has since gone bad must not deadlock free-field edits —
  // the backend renders its own verdict on those, and its error is surfaced verbatim.
  const productChanged = !isEdit || productId !== deal?.product.id;
  const kindChanged = !isEdit || kind !== deal?.kind;

  const productClientError = (() => {
    if (productId === 0) {
      return undefined; // "nothing selected" is handled by disabling Save, not by nagging.
    }
    if (isAttached && productRecurring === 'yearly') {
      return 'A yearly product cannot be attached to a virtual machine — the VM invoice cycle is monthly.';
    }
    if (isEdit && isStandalone && isOnceProduct && deal.product.recurring !== 'once') {
      return 'A standalone deal cannot be moved onto a one-time product — standalone one-shots bill at creation only.';
    }
    if ((productChanged || kindChanged) && isDiscountKind && selectedProduct && !selectedProduct.product_desc.includes('{{amount}}')) {
      return "This product's invoice template lacks {{amount}}, which every discount deal requires — edit the product's invoice description under Admin → Products and Prices first.";
    }
    if (productChanged && isStandalone && selectedProduct) {
      // A standalone deal's wording may use only the auto period tokens: {{vm}}/{{amount}}/{{rate}}
      // are provided by the VM meter's attached-deal context alone, and an unrendered token makes
      // every emit fail loud at billing time — the deal would never produce an invoice line.
      const attachedOnlyTokens = ['{{vm}}', '{{amount}}', '{{rate}}'].filter((t) =>
        selectedProduct.product_desc.includes(t),
      );
      if (attachedOnlyTokens.length > 0) {
        return `This product's invoice template uses ${attachedOnlyTokens.join(' and ')}, which only resolve on VM-attached deals — a standalone agreement's wording may use only the period tokens. Edit the product's invoice description, or create the deal from the virtual machine's overview card.`;
      }
    }
    return undefined;
  })();

  const trimmedValue = value.trim();
  const valueError = (() => {
    if (trimmedValue === '') {
      return 'Value is required.';
    }
    if (!DECIMAL_RE.test(trimmedValue)) {
      return 'Enter a positive amount with up to 4 decimal places (e.g. 300 or 12.5).';
    }
    if (parseFloat(trimmedValue) <= 0) {
      return 'Value must be greater than zero.';
    }
    if (kind === 'discount_percent' && parseFloat(trimmedValue) > 100) {
      return 'A percent rebate cannot exceed 100.';
    }
    return undefined;
  })();

  // Day-level checks the DatePicker also runs live (its `validators`); monthly anchors must keep
  // day ≤ 28 so every billed month contains the anniversary.
  const anchorDateError = (date: Date): string => {
    const candidate = new Date(date);
    candidate.setHours(0, 0, 0, 0);
    // The UNCHANGED persisted anchor is exempt from every date rule: the change-set never re-sends
    // it, and the backend deliberately skips the ≥ today check for an anchor absent from the
    // PATCH — a past-anchored (e.g. deactivated) deal must stay editable on its free fields
    // instead of deadlocking the form on a date the user never touched.
    const candidateStr = `${candidate.getFullYear()}-${String(candidate.getMonth() + 1).padStart(2, '0')}-${String(
      candidate.getDate(),
    ).padStart(2, '0')}`;
    if (isEdit && candidateStr === (deal?.next_billing_at ?? '')) {
      return '';
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (candidate.getTime() < today.getTime()) {
      return 'The anchor date cannot be in the past.';
    }
    if (productRecurring === 'monthly' && candidate.getDate() > 28) {
      return 'For a monthly product, pick a day between 1 and 28.';
    }
    return '';
  };

  // Day-of-month mode: standalone monthly CREATE only. Edit keeps the full DatePicker — the
  // day→date conversion below would silently move an unchanged (possibly past) anchor, and moving
  // an anchor engages resume-after-coverage, so edits should state the exact date deliberately.
  const anchorDayMode = !isEdit && isStandalone && productRecurring === 'monthly';

  // Same rule as VM billing enrollment: the day anchors THIS month while it is still ahead (or
  // today), otherwise NEXT month — the deals API itself takes a full Y-m-d date, so convert here.
  const anchorDateFromDay = (day: number): string => {
    const today = new Date();
    const thisMonth = day >= today.getDate();
    const anchored = new Date(today.getFullYear(), today.getMonth() + (thisMonth ? 0 : 1), day);
    return `${anchored.getFullYear()}-${String(anchored.getMonth() + 1).padStart(2, '0')}-${String(
      anchored.getDate(),
    ).padStart(2, '0')}`;
  };

  // An out-of-range typed day (0, 35, NaN) derives NO date — Date() would silently normalize it
  // into a different month, so it must fall through to the 1–28 error instead.
  const anchorDayValid = anchorDay !== '' && Number.isInteger(anchorDay) && anchorDay >= 1 && anchorDay <= 28;
  const trimmedAnchor = anchorDayMode ? (anchorDayValid ? anchorDateFromDay(anchorDay as number) : '') : anchor.trim();
  const anchorError = (() => {
    if (!anchorRequired) {
      return undefined;
    }
    if (trimmedAnchor === '') {
      return anchorDayMode
        ? 'Pick the billing day of the month (1–28).'
        : 'A standalone recurring deal needs a first billing date.';
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmedAnchor)) {
      return 'Enter the date as YYYY-MM-DD.';
    }
    const parsed = new Date(`${trimmedAnchor}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) {
      return 'Enter a valid date.';
    }
    return anchorDateError(parsed) || undefined;
  })();

  const occForcedToOne = !isEdit && isOnceProduct;
  const occDisabled = isSaving || occForcedToOne || isSpentStandaloneOnce || productId === 0;
  const trimmedOcc = occForcedToOne ? '1' : occurrences.trim();
  const occError = (() => {
    if (occDisabled) {
      return undefined;
    }
    // An attached one-shot may only hold 0 (spent) or 1 (armed) — never unlimited. The backend
    // rejects null on the merged row too, so flag an empty field before submit (reachable by
    // swapping an unlimited monthly deal's product to a once product in edit mode).
    const isEditAttachedOnce = isEdit && isAttached && isOnceProduct;
    if (trimmedOcc === '') {
      return isEditAttachedOnce
        ? 'A one-shot deal can only carry 0 (spent) or 1 (armed) occurrences — not unlimited.'
        : undefined; // empty = unlimited (monthly/yearly products)
    }
    if (!/^\d+$/.test(trimmedOcc)) {
      return 'Enter a whole number of occurrences, or leave empty for unlimited.';
    }
    const n = parseInt(trimmedOcc, 10);
    if (!isEdit && n < 1) {
      return 'A new deal needs at least 1 occurrence — a born-spent deal is refused.';
    }
    if (isEditAttachedOnce && n > 1) {
      return 'A one-shot deal can only carry 0 (spent) or 1 (armed) occurrence.';
    }
    return undefined;
  })();

  // PATCHing the budget to 0 auto-deactivates (the explicit {occurrences_left: 0, active: true}
  // combo is refused) — mirror by forcing the switch off while 0 is entered.
  const occIsZero = trimmedOcc === '0';
  const effectiveActive = occIsZero ? false : active;

  // NumberInput plumbing for the occurrences budget. The state stays the string ('' = unlimited);
  // the numeric view + stepper bounds derive from it: create requires ≥ 1 (born-spent refused),
  // edit allows 0 (deactivates), and an attached one-shot is capped at 1.
  const occMin = isEdit ? 0 : 1;
  const occMax = isEdit && isAttached && isOnceProduct ? 1 : undefined;
  const occNumberValue: number | '' = occForcedToOne ? 1 : /^\d+$/.test(trimmedOcc) ? parseInt(trimmedOcc, 10) : '';
  const stepOccurrences = (next: number) => {
    const clamped = Math.max(occMin, occMax !== undefined ? Math.min(next, occMax) : next);
    setOccurrences(String(clamped));
    clearError('occurrences_left');
  };

  const trimmedNote = note.trim();

  const isFormValid =
    productId > 0 && !productClientError && !valueError && !anchorError && !occError && !isSaving;

  // ---- Edit change-set (identity fields only while never billed; free fields always) ----
  const changes: SpecialDealUpdateRequest = {};
  if (isEdit) {
    if (!identityFrozen) {
      if (productId !== deal.product.id) {
        changes.productID = productId;
      }
      if (kind !== deal.kind) {
        changes.kind = kind;
      }
    }
    if (trimmedValue !== deal.value) {
      changes.value = trimmedValue;
    }
    if (anchorRequired && trimmedAnchor !== (deal.next_billing_at ?? '')) {
      changes.next_billing_at = trimmedAnchor;
    }
    if (!occDisabled) {
      const occCurrent = deal.occurrences_left === null ? '' : String(deal.occurrences_left);
      if (trimmedOcc !== occCurrent) {
        changes.occurrences_left = trimmedOcc === '' ? null : parseInt(trimmedOcc, 10);
      }
    }
    if (trimmedNote !== (deal.note ?? '')) {
      changes.note = trimmedNote === '' ? null : trimmedNote;
    }
    if (changes.occurrences_left === 0) {
      // The API auto-deactivates on a 0 budget; sending `active` alongside is redundant at best.
    } else if (!isSpentStandaloneOnce && effectiveActive !== deal.active) {
      changes.active = effectiveActive;
    }
  }
  const hasChanges = Object.keys(changes).length > 0;

  const notifyError = (description: string) => {
    addNotification({
      title: isEdit ? 'Could not update agreement' : 'Could not create agreement',
      srTitle: 'Error alert',
      description,
      variant: 'danger',
    });
    setDrawerOpen(true);
  };

  // Spread the backend's field-keyed map onto the form. Cross-field violations (`data.body`) and
  // client errors (no client field in this form) go to the general alert; everything else lands
  // next to its field. Message-only errors ("Deal {id} not found", …) also go general.
  const applyErrors = (map: SpecialDealFieldErrorMap | undefined, message: string | undefined, fallback: string) => {
    const d = map ?? {};
    const attachmentError = d.parent_id ?? d.parent_type;
    const anyField =
      d.productID || d.kind || d.value || attachmentError || d.next_billing_at || d.occurrences_left || d.note || d.active;
    setErrors({
      productID: d.productID,
      kind: d.kind,
      value: d.value,
      attachment: attachmentError,
      next_billing_at: d.next_billing_at,
      occurrences_left: d.occurrences_left,
      note: d.note,
      active: d.active,
      general: d.body ?? d.clientID ?? (anyField ? undefined : (message ?? fallback)),
    });
    notifyError(
      d.productID ??
        d.kind ??
        d.value ??
        attachmentError ??
        d.next_billing_at ??
        d.occurrences_left ??
        d.note ??
        d.active ??
        d.body ??
        d.clientID ??
        message ??
        fallback,
    );
  };

  const handleCreate = async () => {
    const requestBody: SpecialDealCreateRequest = {
      clientID: clientId,
      productID: productId,
      kind,
      value: trimmedValue,
    };
    if (attachedParent) {
      requestBody.parent_type = attachedParent.parentType;
      requestBody.parent_id = attachedParent.parentId;
    }
    if (anchorRequired) {
      requestBody.next_billing_at = trimmedAnchor;
    }
    if (isOnceProduct) {
      requestBody.occurrences_left = 1; // once products default to and REQUIRE exactly 1 — be explicit.
    } else if (trimmedOcc !== '') {
      requestBody.occurrences_left = parseInt(trimmedOcc, 10);
    }
    if (trimmedNote !== '') {
      requestBody.note = trimmedNote;
    }

    const response = await apiFetch('/admin/billing/specials', {
      method: 'POST',
      body: JSON.stringify(requestBody),
    });
    // Branch on the JSend body.status, NEVER response.ok.
    const body = (await response.json().catch(() => null)) as ApiResponseAdminSpecialCreate | null;
    if (body?.status === 'success' && body.data?.special) {
      const created = body.data.special;
      const emitted = body.data.emitted_event_id ?? null;
      addNotification({
        title: 'Agreement created',
        srTitle: 'Success alert',
        description:
          emitted !== null
            ? `${created.product.label} for ${clientLabel} was billed at creation — invoice line #${emitted} is pending and lands on the next billing run.`
            : `${created.product.label} for ${clientLabel} has been created.`,
        variant: 'success',
      });
      setDrawerOpen(true);
      onSaved();
      onClose();
      return;
    }
    applyErrors(body?.data, body?.message, 'Could not create the agreement. Please try again.');
  };

  const handleUpdate = async () => {
    if (!isEdit || !hasChanges) {
      return;
    }
    const response = await apiFetch(`/admin/billing/specials/${deal.id}`, {
      method: 'PATCH',
      body: JSON.stringify(changes),
    });
    // Branch on the JSend body.status, NEVER response.ok.
    const body = (await response.json().catch(() => null)) as ApiResponseAdminSpecialUpdate | null;
    if (body?.status === 'success' && body.data?.special) {
      addNotification({
        title: 'Agreement updated',
        srTitle: 'Success alert',
        description: `${body.data.special.product.label} for ${clientLabel} has been updated.`,
        variant: 'success',
      });
      setDrawerOpen(true);
      onSaved();
      onClose();
      return;
    }
    applyErrors(body?.data, body?.message, 'Could not update the agreement. Please try again.');
  };

  const handleSave = async () => {
    if (!isFormValid || (isEdit && !hasChanges)) {
      return;
    }
    setIsSaving(true);
    setErrors({});
    try {
      await (isEdit ? handleUpdate() : handleCreate());
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred.';
      setErrors({ general: message });
      notifyError(message);
    } finally {
      setIsSaving(false);
    }
  };

  const kindOptions: { value: SpecialDealKind; label: string; description: string }[] = [
    { value: 'charge', label: 'Charge', description: 'An extra amount billed to the organization.' },
    {
      value: 'discount_fixed',
      label: 'Fixed rebate',
      description: 'A euro amount deducted from the invoice.',
    },
    {
      value: 'discount_percent',
      label: 'Percent rebate',
      description: 'A percentage of the invoiced base, computed at billing time.',
    },
  ];

  const modalTitle = isEdit
    ? `Modify agreement — ${deal.product.label}`
    : attachedParent
      ? `New special agreement — ${attachedParent.parentName ?? `VM #${attachedParent.parentId}`}`
      : 'New special agreement';

  return (
    <Modal variant="medium" isOpen={isOpen} onClose={onClose} aria-label={modalTitle}>
      <ModalHeader title={modalTitle} />
      <ModalBody>
        <Form
          onSubmit={(event) => {
            event.preventDefault();
            void handleSave();
          }}
        >
          {errors.general && (
            <Alert variant="danger" isInline title={isEdit ? 'Could not update agreement' : 'Could not create agreement'}>
              {errors.general}
            </Alert>
          )}

          {identityFrozen && (
            <Alert variant="info" isInline title="Identity fields are frozen">
              This deal has {deal.billed_event_count} billed event
              {deal.billed_event_count === 1 ? '' : 's'} on record — its product, kind and attachment can no
              longer change (the invoiced lines reference them). Value, occurrences, the note and the
              active switch stay editable.
            </Alert>
          )}

          <FormGroup label="Organization" fieldId="deal-client">
            <TextInput id="deal-client" value={clientLabel} isDisabled aria-label="Billed organization (fixed)" />
            <FormHelperText>
              <HelperText>
                <HelperTextItem>
                  The organization this agreement bills — switch organizations to target another one.
                </HelperTextItem>
              </HelperText>
            </FormHelperText>
          </FormGroup>

          {isAttached && (
            <FormGroup label="Attached to" fieldId="deal-attachment">
              <TextInput id="deal-attachment" value={attachmentDisplay} isDisabled aria-label="Attached parent (locked)" />
              <FormHelperText>
                <HelperText>
                  <HelperTextItem variant={errors.attachment ? 'error' : 'default'}>
                    {errors.attachment ?? "The agreement's line rides this virtual machine's monthly invoice."}
                  </HelperTextItem>
                </HelperText>
              </FormHelperText>
            </FormGroup>
          )}

          <FormGroup label="Product" isRequired fieldId="deal-product">
            <Select
              id="deal-product"
              isOpen={isProductOpen}
              selected={productId}
              onSelect={(_event, selection) => {
                setProductId(Number(selection));
                setIsProductOpen(false);
                clearError('productID');
              }}
              onOpenChange={(open) => setIsProductOpen(open)}
              toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
                <MenuToggle
                  ref={toggleRef}
                  onClick={() => setIsProductOpen(!isProductOpen)}
                  isExpanded={isProductOpen}
                  isDisabled={isSaving || identityFrozen || productsLoading}
                  isFullWidth
                  status={errors.productID || productClientError ? 'danger' : undefined}
                  aria-label="Select the special product this agreement bills"
                >
                  {productsLoading ? 'Loading products…' : (selectedProductLabel ?? 'Select a product')}
                </MenuToggle>
              )}
              shouldFocusToggleOnSelect
            >
              <SelectList>
                {specialProducts.length === 0 && (
                  <SelectOption value={0} isDisabled>
                    No active products in the “{SPECIAL_CATEGORY_NAME}” category
                  </SelectOption>
                )}
                {specialProducts.map((p) => {
                  const yearlyBlocked = isAttached && p.recurring === 'yearly';
                  return (
                    <SelectOption
                      key={p.id}
                      value={p.id}
                      isSelected={p.id === productId}
                      isDisabled={yearlyBlocked}
                      description={`${p.product_key} · ${formatRecurring(p.recurring)}${
                        yearlyBlocked ? ' — cannot be attached (the VM cycle is monthly)' : ''
                      }${p.tenant_visible ? '' : ' · hidden from customers'}`}
                    >
                      {p.label}
                    </SelectOption>
                  );
                })}
              </SelectList>
            </Select>
            <FormHelperText>
              <HelperText>
                <HelperTextItem
                  variant={errors.productID || productClientError || productsError ? 'error' : 'default'}
                >
                  {errors.productID ??
                    productClientError ??
                    (productsError
                      ? `Could not load the product catalog: ${productsError}`
                      : `Active products of the “${SPECIAL_CATEGORY_NAME}” category. The product supplies the invoice wording and the billing cadence; the agreement supplies the amount.`)}
                </HelperTextItem>
              </HelperText>
            </FormHelperText>
          </FormGroup>

          <FormGroup label="Kind" isRequired role="radiogroup" fieldId="deal-kind">
            {kindOptions.map((option) => {
              const discountBlocked = option.value !== 'charge' && isStandalone;
              return (
                <Radio
                  key={option.value}
                  id={`deal-kind-${option.value}`}
                  name="deal-kind"
                  label={option.label}
                  description={
                    discountBlocked
                      ? `${option.description} Rebates must be attached to a product — create them from the virtual machine's overview card.`
                      : option.description
                  }
                  isChecked={kind === option.value}
                  isDisabled={isSaving || identityFrozen || discountBlocked}
                  onChange={(_event, checked) => {
                    if (checked) {
                      setKind(option.value);
                      clearError('kind');
                    }
                  }}
                />
              );
            })}
            {errors.kind && (
              <FormHelperText>
                <HelperText>
                  <HelperTextItem variant="error">{errors.kind}</HelperTextItem>
                </HelperText>
              </FormHelperText>
            )}
          </FormGroup>

          <FormGroup
            label={kind === 'discount_percent' ? 'Rate (%)' : 'Amount (€, excl. VAT)'}
            isRequired
            fieldId="deal-value"
          >
            <TextInput
              id="deal-value"
              type="text"
              inputMode="decimal"
              value={value}
              onChange={(_event, newValue) => {
                setValue(newValue);
                clearError('value');
              }}
              validated={errors.value || valueError ? 'error' : 'default'}
              isDisabled={isSaving}
              aria-label={kind === 'discount_percent' ? 'Rebate rate in percent' : 'Amount in euros, excluding VAT'}
            />
            <FormHelperText>
              <HelperText>
                <HelperTextItem variant={errors.value || (trimmedValue !== '' && valueError) ? 'error' : 'default'}>
                  {errors.value ??
                    (trimmedValue !== '' ? valueError : undefined) ??
                    (kind === 'discount_percent'
                      ? 'Nominal rate in (0, 100]. The euro amount is computed on the invoiced base at billing time, and clamped so the invoice never nets negative.'
                      : kind === 'discount_fixed'
                        ? 'Always positive — the rebate sign comes from the kind. Clamped at billing time so the invoice never nets negative.'
                        : 'Always positive, up to 4 decimal places. Changes apply from the next emission — already-emitted lines keep their snapshot.')}
                </HelperTextItem>
              </HelperText>
            </FormHelperText>
          </FormGroup>

          {anchorRequired && anchorDayMode && (
            <FormGroup label="Billing day of the month" isRequired fieldId="deal-anchor">
              <NumberInput
                value={anchorDay}
                min={1}
                max={28}
                onMinus={() => {
                  setAnchorDay(anchorDay === '' ? Math.min(new Date().getDate(), 28) : Math.max(1, anchorDay - 1));
                  clearError('next_billing_at');
                }}
                onPlus={() => {
                  setAnchorDay(anchorDay === '' ? Math.min(new Date().getDate(), 28) : Math.min(28, anchorDay + 1));
                  clearError('next_billing_at');
                }}
                onChange={(event) => {
                  const raw = (event.target as HTMLInputElement).value;
                  setAnchorDay(raw === '' ? '' : Number(raw));
                  clearError('next_billing_at');
                }}
                isDisabled={isSaving}
                validated={errors.next_billing_at || anchorError ? 'error' : 'default'}
                inputAriaLabel="Billing day of the month"
                minusBtnAriaLabel="Earlier day"
                plusBtnAriaLabel="Later day"
                inputProps={{ id: 'deal-anchor' }}
              />
              <FormHelperText>
                <HelperText>
                  <HelperTextItem variant={errors.next_billing_at || anchorError ? 'error' : 'default'}>
                    {errors.next_billing_at ??
                      anchorError ??
                      `Day the monthly invoice is anchored to (1–28) — align it with the client's existing recurring invoices to avoid over- or under-invoicing. A day already past this month anchors next month — first billing on ${formatBareDateNumeric(trimmedAnchor)}.`}
                  </HelperTextItem>
                </HelperText>
              </FormHelperText>
            </FormGroup>
          )}
          {anchorRequired && !anchorDayMode && (
            <FormGroup label="First billing date" isRequired fieldId="deal-anchor">
              <DatePicker
                // The id must land on the inner text input (inputProps), not the wrapper div a
                // top-level id would target, or the FormGroup's <label for="deal-anchor"> points
                // at a non-labelable element.
                inputProps={{ id: 'deal-anchor' }}
                value={anchor}
                onChange={(_event, newValue) => {
                  setAnchor(newValue);
                  clearError('next_billing_at');
                }}
                validators={[anchorDateError]}
                placeholder="YYYY-MM-DD"
                aria-label="First billing date"
                isDisabled={isSaving}
              />
              <FormHelperText>
                <HelperText>
                  <HelperTextItem variant={errors.next_billing_at ? 'error' : 'default'}>
                    {errors.next_billing_at ??
                      (productRecurring === 'monthly'
                        ? 'The billing anniversary — today or later, on a day between 1 and 28. Each cycle covers one month from this date.'
                        : 'The billing anniversary — today or later. Each cycle covers one year from this date.')}
                  </HelperTextItem>
                </HelperText>
              </FormHelperText>
            </FormGroup>
          )}

          {!isEdit && isStandalone && isOnceProduct && (
            <Alert variant="warning" isInline title="This will be invoiced at the next billing run">
              A standalone agreement on a one-time product bills immediately: its invoice line is emitted
              at creation (as pending) and the agreement is recorded spent right away.
            </Alert>
          )}

          <FormGroup label="Occurrences" fieldId="deal-occurrences">
            <Flex alignItems={{ default: 'alignItemsCenter' }} spaceItems={{ default: 'spaceItemsSm' }}>
              <FlexItem>
                <NumberInput
                  value={occNumberValue}
                  min={occMin}
                  max={occMax}
                  onMinus={() => stepOccurrences(occNumberValue === '' ? occMin : occNumberValue - 1)}
                  onPlus={() => stepOccurrences(occNumberValue === '' ? 1 : occNumberValue + 1)}
                  onChange={(event) => {
                    // An emptied input means "unlimited" — keep it '' rather than coercing to a number.
                    setOccurrences((event.target as HTMLInputElement).value);
                    clearError('occurrences_left');
                  }}
                  isDisabled={occDisabled}
                  validated={errors.occurrences_left || occError ? 'error' : 'default'}
                  widthChars={10}
                  inputAriaLabel="Number of billing occurrences"
                  minusBtnAriaLabel="Decrease occurrences"
                  plusBtnAriaLabel="Increase occurrences"
                  inputProps={{ id: 'deal-occurrences', placeholder: isOnceProduct ? '1' : 'Unlimited' }}
                />
              </FlexItem>
              {/* Once a number is set, the steppers alone can't reach the empty (= unlimited) state
                  again — this is the explicit way back. Hidden where unlimited is illegal (once
                  products) or the field is frozen. */}
              {!occDisabled && !isOnceProduct && trimmedOcc !== '' && (
                <FlexItem>
                  <Button
                    variant="link"
                    isInline
                    onClick={() => {
                      setOccurrences('');
                      clearError('occurrences_left');
                    }}
                  >
                    Reset to unlimited
                  </Button>
                </FlexItem>
              )}
            </Flex>
            <FormHelperText>
              <HelperText>
                <HelperTextItem variant={errors.occurrences_left || occError ? 'error' : 'default'}>
                  {errors.occurrences_left ??
                    occError ??
                    (isSpentStandaloneOnce
                      ? 'Billed at creation — a standalone one-time agreement stays spent.'
                      : isOnceProduct
                        ? isAttached && isEdit
                          ? '0 = spent; 1 = armed for the next invoice (re-arming a spent one-shot is allowed).'
                          : 'A one-time product bills exactly once.'
                        : isEdit
                          ? 'Empty = until deactivated; N = the next N cycles. Setting 0 deactivates the agreement.'
                          : 'Empty = until deactivated; 1 = one-shot; N = the first N cycles.')}
                </HelperTextItem>
              </HelperText>
            </FormHelperText>
          </FormGroup>

          {isEdit && (
            <FormGroup label="Status" fieldId="deal-active">
              <Switch
                id="deal-active"
                label="Active"
                isChecked={effectiveActive}
                onChange={(_event, checked) => {
                  setActive(checked);
                  clearError('active');
                }}
                isDisabled={isSaving || isSpentStandaloneOnce || occIsZero}
              />
              <FormHelperText>
                <HelperText>
                  <HelperTextItem variant={errors.active ? 'error' : 'default'}>
                    {errors.active ??
                      (occIsZero
                        ? 'A 0 budget deactivates the agreement automatically.'
                        : 'Inactive agreements never emit. Deactivation stops future lines only — an already-emitted pending line still lands on its invoice.')}
                  </HelperTextItem>
                </HelperText>
              </FormHelperText>
            </FormGroup>
          )}

          <FormGroup label="Internal note" fieldId="deal-note">
            <TextArea
              id="deal-note"
              value={note}
              maxLength={NOTE_MAX}
              rows={2}
              onChange={(_event, newValue) => {
                setNote(newValue);
                clearError('note');
              }}
              validated={errors.note ? 'error' : 'default'}
              isDisabled={isSaving}
              aria-label="Internal staff note"
            />
            <FormHelperText>
              <HelperText>
                <HelperTextItem variant={errors.note ? 'error' : 'default'}>
                  {errors.note ?? 'Staff-only memo (e.g. the commercial context). Never shown to the customer, never on an invoice.'}
                </HelperTextItem>
              </HelperText>
            </FormHelperText>
          </FormGroup>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button
          variant="primary"
          onClick={() => void handleSave()}
          isLoading={isSaving}
          isDisabled={isSaving || !isFormValid || (isEdit && !hasChanges)}
        >
          {isEdit ? 'Save' : 'Create'}
        </Button>
        <Button variant="link" onClick={onClose} isDisabled={isSaving}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default SpecialDealModal;
